import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppDropdown from "../components/AppDropdown";
import * as XLSX from "xlsx";
import {
  getMerchantTransactions,
  getMerchantTransactionStats,
} from "../api/api";

/* ─── Status meta ────────────────────────────────────────────── */
const STATUS_META = {
  PAID: { bg: "#dcfce7", color: "#166534", dot: "#16a34a" },
  ACTIVE: { bg: "#e0f2fe", color: "#075985", dot: "#0ea5e9" },
  PENDING: { bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  FAILED: { bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
};

/* ─── Stat tile ──────────────────────────────────────────────── */
function StatTile({ label, value, accent, sub }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        padding: "1.1rem 1.3rem",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
        borderLeft: `4px solid ${accent}`,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        transition: "transform .2s, box-shadow .2s",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow =
          "0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)";
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 11,
          fontWeight: 600,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: ".06em",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 28,
          fontWeight: 700,
          color: "#111827",
          lineHeight: 1,
        }}
      >
        {value ?? "—"}
      </p>
      {sub && (
        <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{sub}</p>
      )}
    </div>
  );
}

/* ─── Status badge ───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const m = STATUS_META[status] || {
    bg: "#f3f4f6",
    color: "#374151",
    dot: "#9ca3af",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        background: m.bg,
        color: m.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: "50%", background: m.dot }}
      />
      {status}
    </span>
  );
}

/* ─── Paginator ──────────────────────────────────────────────── */
function Paginator({ page, totalPages, onChange }) {
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);
  const btn = (label, target, disabled) => (
    <button
      key={label}
      onClick={() => !disabled && onChange(target)}
      disabled={disabled}
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        border: "0.5px solid #e5e7eb",
        background: target === page ? "#534AB7" : "#fff",
        color: target === page ? "#fff" : disabled ? "#d1d5db" : "#374151",
        fontWeight: target === page ? 700 : 400,
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        transition: "all .15s",
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      {btn("← Prev", page - 1, page === 1)}
      {start > 1 && (
        <>
          {btn(1, 1, false)}
          <span style={{ color: "#9ca3af" }}>…</span>
        </>
      )}
      {pages.map((p) => btn(p, p, false))}
      {end < totalPages && (
        <>
          <span style={{ color: "#9ca3af" }}>…</span>
          {btn(totalPages, totalPages, false)}
        </>
      )}
      {btn("Next →", page + 1, page === totalPages)}
    </div>
  );
}

/* ─── Custom Calendar ────────────────────────────────────────── */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function CalendarDropdown({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  accentColor = "#534AB7",
}) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() =>
    value ? new Date(value).getFullYear() : new Date().getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(() =>
    value ? new Date(value).getMonth() : new Date().getMonth(),
  );
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = value ? new Date(value + "T00:00:00") : null;
  const minD = minDate ? new Date(minDate + "T00:00:00") : null;
  const maxD = maxDate ? new Date(maxDate + "T00:00:00") : null;

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    if (minD && d < minD) return true;
    if (maxD && d > maxD) return true;
    return false;
  };

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const selectDay = (day) => {
    const mm = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    onChange(`${viewYear}-${mm}-${dd}`);
    setOpen(false);
  };

  const isSelected = (day) => {
    if (!selected) return false;
    return (
      selected.getFullYear() === viewYear &&
      selected.getMonth() === viewMonth &&
      selected.getDate() === day
    );
  };

  const isToday = (day) => {
    const t = new Date();
    return (
      t.getFullYear() === viewYear &&
      t.getMonth() === viewMonth &&
      t.getDate() === day
    );
  };

  const displayValue = value
    ? new Date(value + "T00:00:00").toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : label;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger pill */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 40,
          padding: "0 14px",
          borderRadius: 10,
          border: `0.5px solid ${open ? accentColor : "#e5e7eb"}`,
          background: value ? accentColor + "0d" : "#fff",
          color: value ? accentColor : "#9ca3af",
          fontSize: 13,
          fontWeight: value ? 600 : 400,
          cursor: "pointer",
          transition: "all .15s",
          whiteSpace: "nowrap",
          boxShadow: open ? `0 0 0 3px ${accentColor}22` : "none",
        }}
      >
        <span style={{ fontSize: 15 }}>📅</span>
        {displayValue}
        {value && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            style={{
              marginLeft: 2,
              color: accentColor,
              fontSize: 16,
              lineHeight: 1,
              fontWeight: 400,
            }}
          >
            ×
          </span>
        )}
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 999,
            background: "#fff",
            borderRadius: 16,
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.06)",
            padding: "16px",
            minWidth: 280,
            userSelect: "none",
          }}
        >
          {/* Month / Year nav */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <button
              onClick={prevMonth}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: "0.5px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#374151",
              }}
            >
              ‹
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <AppDropdown
                options={MONTHS.map((m, i) => ({ value: i, label: m }))}
                value={viewMonth}
                onChange={(v) => setViewMonth(Number(v))}
              />
              <AppDropdown
                options={Array.from(
                  { length: 10 },
                  (_, i) => new Date().getFullYear() - 5 + i,
                ).map((y) => ({ value: y, label: String(y) }))}
                value={viewYear}
                onChange={(v) => setViewYear(Number(v))}
              />
            </div>
            <button
              onClick={nextMonth}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: "0.5px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#374151",
              }}
            >
              ›
            </button>
          </div>

          {/* Day headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              marginBottom: 4,
            }}
          >
            {DAYS.map((d) => (
              <div
                key={d}
                style={{
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  padding: "4px 0",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 2,
            }}
          >
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const disabled = isDisabled(day);
              const sel = isSelected(day);
              const today = isToday(day);
              return (
                <button
                  key={day}
                  onClick={() => !disabled && selectDay(day)}
                  disabled={disabled}
                  style={{
                    height: 34,
                    borderRadius: 8,
                    border:
                      today && !sel ? `1.5px solid ${accentColor}` : "none",
                    background: sel ? accentColor : "transparent",
                    color: sel
                      ? "#fff"
                      : disabled
                        ? "#d1d5db"
                        : today
                          ? accentColor
                          : "#374151",
                    fontWeight: sel || today ? 700 : 400,
                    fontSize: 13,
                    cursor: disabled ? "default" : "pointer",
                    transition: "background .1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!sel && !disabled)
                      e.currentTarget.style.background = accentColor + "18";
                  }}
                  onMouseLeave={(e) => {
                    if (!sel) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: "0.5px solid #f3f4f6",
              display: "flex",
              gap: 6,
            }}
          >
            <button
              onClick={() => {
                const t = new Date();
                onChange(
                  `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`,
                );
                setOpen(false);
              }}
              disabled={minD && new Date() < minD}
              style={{
                flex: 1,
                padding: "5px 0",
                borderRadius: 7,
                border: `0.5px solid ${accentColor}`,
                background: accentColor + "10",
                color: accentColor,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Today
            </button>
            <button
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              style={{
                flex: 1,
                padding: "5px 0",
                borderRadius: 7,
                border: "0.5px solid #e5e7eb",
                background: "#f9fafb",
                color: "#6b7280",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Date Range Picker (two CalendarDropdowns) ──────────────── */
function DateRangePicker({
  dateFrom,
  dateTo,
  onFromChange,
  onToChange,
  onClear,
}) {
  const today = new Date().toISOString().split("T")[0];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <CalendarDropdown
        value={dateFrom}
        onChange={onFromChange}
        maxDate={today}
        label="From date"
        accentColor="#534AB7"
      />
      <span style={{ color: "#d1d5db", fontSize: 14, fontWeight: 700 }}>→</span>
      <CalendarDropdown
        value={dateTo}
        minDate={dateFrom || undefined}
        maxDate={today}
        onChange={onToChange}
        minDate={dateFrom || undefined}
        label="To date"
        accentColor="#534AB7"
      />
      {(dateFrom || dateTo) && (
        <button
          onClick={onClear}
          style={{
            height: 40,
            padding: "0 12px",
            borderRadius: 10,
            border: "0.5px solid #fee2e2",
            background: "#fff5f5",
            color: "#ef4444",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Clear dates
        </button>
      )}
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────── */
const fmtAmt = (n) =>
  Number(n ?? 0).toLocaleString("en-PK", { minimumFractionDigits: 2 });
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-PK", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

/* ─── Excel export ───────────────────────────────────────────── */
const exportToExcel = (txns, merchant, dateFrom, dateTo) => {
  if (!txns.length) return;
  const rows = txns.map((t, i) => ({
    "#": i + 1,
    RRN: t.info?.rrn || "",
    STAN: t.info?.stan || "",
    "Amount (Rs)": Number(t.paymentDetails?.instructedAmount ?? 0),
    Type: t.paymentDetails?.transactionType || "",
    "QR Type": t.isStatic ? "Static" : "Dynamic",
    "Payer Name": t.payerDetails?.additionalRequiredDetails || "",
    "Payer IBAN": t.payerDetails?.identificationDetails?.customerLabel || "",
    "Execution Time": t.paymentDetails?.executionDateTime
      ? new Date(t.paymentDetails.executionDateTime).toLocaleString()
      : new Date(t.createdAt).toLocaleString(),
    Status: t.status,
    "Sub Dept": t.subDept || "",
    "Channel ID": t.merchantChannelId || "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 4 },
    { wch: 14 },
    { wch: 8 },
    { wch: 14 },
    { wch: 14 },
    { wch: 10 },
    { wch: 20 },
    { wch: 28 },
    { wch: 22 },
    { wch: 10 },
    { wch: 10 },
    { wch: 18 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    ws,
    `Txns_${merchant.merchantID}`.slice(0, 31),
  );
  const rangePart =
    dateFrom && dateTo
      ? `_${dateFrom}_to_${dateTo}`
      : dateFrom
        ? `_from_${dateFrom}`
        : dateTo
          ? `_to_${dateTo}`
          : "";
  XLSX.writeFile(wb, `transactions_${merchant.merchantID}${rangePart}.xlsx`);
};

/* ─── Main page ──────────────────────────────────────────────── */
const MerchantTransactions = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const merchant = location.state?.merchant || {};
  const merchantID = merchant.merchantID;

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  /* ── stats ───────────────────────────────────────────────── */
  useEffect(() => {
    if (!merchantID) return;
    (async () => {
      try {
        const res = await getMerchantTransactionStats(merchantID);
        if (res?.success) setStats(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setStatsLoading(false);
      }
    })();
  }, [merchantID]);

  /* ── fetch transactions ──────────────────────────────────── */
  const fetchTxns = useCallback(
    async (p, q, s, t, from, to) => {
      if (!merchantID) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: p, limit: 100 });
        if (q) params.set("search", q);
        if (s) params.set("status", s);
        if (t) params.set("type", t);
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        const res = await getMerchantTransactions(merchantID, params);
        if (res?.success) {
          setTxns(res.data);
          setPagination(res.pagination);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [merchantID],
  );

  useEffect(() => {
    fetchTxns(page, appliedSearch, statusFilter, typeFilter, dateFrom, dateTo);
  }, [
    page,
    appliedSearch,
    statusFilter,
    typeFilter,
    dateFrom,
    dateTo,
    fetchTxns,
  ]);

  const handleSearch = () => {
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };
  const handleClear = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(1);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };
  const handleDateClear = () => {
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  /* ── export ──────────────────────────────────────────────── */
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 10000 });
      if (appliedSearch) params.set("search", appliedSearch);
      if (statusFilter) params.set("status", statusFilter);
      if (typeFilter) params.set("type", typeFilter);
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);
      const res = await getMerchantTransactions(merchantID, params);
      if (res?.success) exportToExcel(res.data, merchant, dateFrom, dateTo);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  /* ── stat tiles ──────────────────────────────────────────── */
  const statTiles = stats
    ? [
        { label: "Total Transactions", value: stats.total, accent: "#534AB7" },
        { label: "Successful (Paid)", value: stats.paid, accent: "#16a34a" },
        { label: "Active QRs", value: stats.active, accent: "#0891b2" },
        { label: "Pending", value: stats.pending, accent: "#eab308" },
       
        {
          label: "Total Volume",
          value: `Rs ${fmtAmt(stats.totalVolume)}`,
          accent: "#534AB7",
        },
      ]
    : [];

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div
      style={{
        marginLeft: isSidebarOpen ? 220 : 0,
        minHeight: "100vh",
        background: "#f5f5fb",
        padding: "2rem",
        fontFamily: "'Calibri', sans-serif",
        transition: "margin-left .3s",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 4,
            }}
          >
            
            <h1
              style={{
                margin: 0,
                fontSize: "1.6rem",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              💳 Transactions
            </h1>
          </div>
          
        </div>

        <button
          onClick={handleExport}
          disabled={exporting || loading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            height: 42,
            padding: "0 20px",
            borderRadius: 10,
            background: exporting ? "#9ca3af" : "#16a34a",
            color: "#fff",
            border: "none",
            fontSize: 13,
            fontWeight: 700,
            cursor: exporting ? "not-allowed" : "pointer",
            transition: "opacity .15s",
            boxShadow: "0 2px 8px rgba(22,163,74,0.25)",
          }}
          onMouseEnter={(e) => {
            if (!exporting) e.currentTarget.style.opacity = ".88";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          <span style={{ fontSize: 16 }}>⬇️</span>
          {exporting ? "Exporting…" : "Export Excel"}
        </button>
      </div>

      {/* Stat tiles */}
      {!statsLoading && statTiles.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
            gap: "1rem",
            marginBottom: "1.75rem",
          }}
        >
          {statTiles.map((t, i) => (
            <StatTile key={i} {...t} />
          ))}
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <span
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 15,
              color: "#9ca3af",
              pointerEvents: "none",
            }}
          >
            🔍
          </span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search RRN, STAN, Channel ID…"
            style={{
              width: "100%",
              paddingLeft: 36,
              paddingRight: searchInput ? 32 : 12,
              height: 40,
              border: "0.5px solid #e5e7eb",
              borderRadius: 10,
              fontSize: 14,
              color: "#111827",
              background: "#fff",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
          {searchInput && (
            <button
              onClick={handleClear}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9ca3af",
                fontSize: 16,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        <button
          onClick={handleSearch}
          style={{
            height: 40,
            padding: "0 20px",
            borderRadius: 10,
            background: "#534AB7",
            color: "#fff",
            border: "none",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = ".85";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          Search
        </button>

        <AppDropdown
          options={[
            { value: "", label: "All statuses" },
            ...["PAID", "ACTIVE", "PENDING"].map((s) => ({ value: s, label: s })),
          ]}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        />

       

        {/* Custom date range picker */}
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onFromChange={(v) => {
            setDateFrom(v);
            setPage(1);
          }}
          onToChange={(v) => {
            setDateTo(v);
            setPage(1);
          }}
          onClear={handleDateClear}
        />

        {appliedSearch && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 20,
              background: "#534AB715",
              color: "#534AB7",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            "{appliedSearch}"
            <button
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                color: "#534AB7",
                cursor: "pointer",
                fontSize: 14,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </span>
        )}

        <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280" }}>
          {loading
            ? "Loading…"
            : `${pagination.total.toLocaleString()} results`}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8f7ff",
                  borderBottom: "0.5px solid #e5e7eb",
                }}
              >
                {[
                  "#",
                  "RRN",
                  "STAN",
                  "Amount",
                  
                  "QR Type",
                  "Payer",
                  "Execution Time",
                  "Status",
                ].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "13px 16px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#534AB7",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "#9ca3af",
                    }}
                  >
                    Loading transactions…
                  </td>
                </tr>
              ) : txns.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "#9ca3af",
                    }}
                  >
                    No transactions found.
                  </td>
                </tr>
              ) : (
                txns.map((t, idx) => (
                  <tr
                    key={t._id}
                    style={{
                      borderBottom: "0.5px solid #f3f4f6",
                      background: idx % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f5f3ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        idx % 2 === 0 ? "#fff" : "#fafafa";
                    }}
                  >
                    <td style={td}>{(pagination.page - 1) * 100 + idx + 1}</td>

                    <td style={td}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          color: "#111827",
                          fontSize: 13,
                          fontFamily: "monospace",
                        }}
                      >
                        {t.info?.rrn || "—"}
                      </p>
                    </td>

                    <td style={td}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          color: "#374151",
                          fontFamily: "monospace",
                        }}
                      >
                        {t.info?.stan || "—"}
                      </p>
                    </td>

                    <td style={td}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#111827",
                        }}
                      >
                        Rs {fmtAmt(t.paymentDetails?.instructedAmount)}
                      </p>
                    </td>

                    

                    <td style={{ ...td, fontSize: 12 }}>
                      <span
                        style={{
                          padding: "2px 10px",
                          borderRadius: 6,
                          background: t.isStatic ? "#fef9c3" : "#f0fdf4",
                          color: t.isStatic ? "#854d0e" : "#166534",
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      >
                        {t.isStatic ? "🔒 Static" : "⚡ Dynamic"}
                      </span>
                    </td>

                    <td style={td}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 12,
                          color: "#111827",
                          fontWeight: 600,
                        }}
                      >
                        {t.payerDetails?.additionalRequiredDetails || "—"}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          color: "#9ca3af",
                          fontFamily: "monospace",
                        }}
                      >
                        {t.payerDetails?.identificationDetails?.customerLabel ||
                          ""}
                      </p>
                    </td>

                    <td style={{ ...td, fontSize: 12, color: "#6b7280" }}>
                      {fmtDate(
                        t.paymentDetails?.executionDateTime || t.createdAt,
                      )}
                    </td>

                    <td style={td}>
                      <StatusBadge status={t.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && pagination.totalPages > 1 && (
          <div
            style={{
              padding: "14px 20px",
              borderTop: "0.5px solid #f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              Page {pagination.page} of {pagination.totalPages} —{" "}
              {pagination.total.toLocaleString()} total
            </p>
            <Paginator
              page={pagination.page}
              totalPages={pagination.totalPages}
              onChange={(p) => setPage(p)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const td = { padding: "12px 16px", verticalAlign: "top", fontSize: 13 };

export default MerchantTransactions;
